import { useAsyncData } from '#imports'
import { computed, ref, watch, watchEffect } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useDebounceFn } from '@vueuse/core'
import algoliasearch from 'algoliasearch'

import { SWAP_INFO_MESSAGE } from '../../../constants'
import type { BuybackProduct, Scope } from '../api/types'

export interface AlgoliaHit {
  maxPriceEstimate: Price | null
  productLine: string
  objectID: string
  storage: string
  model: string
  brand: string
  category: string
  image: string
  slug: string
}

export function algoliaHitToBuybackProduct(hit: AlgoliaHit): BuybackProduct {
  return {
    model: hit.model,
    brand: hit.brand,
    category: hit.category,
    id: hit.objectID,
    price: hit.maxPriceEstimate,
    productLine: hit.productLine,
    slug: hit.slug,
    image: hit.image,
  }
}

export function getBuybackSearchConfiguartion(scope: Scope) {
  const $httpFetch = useHttpFetch()
  const key = getAsyncDataKey('buyback-search-configuration', { scope })

  return useAsyncData(key, () => {
    return $httpFetch('/buyback-funnel/api/v1/search/config/:scope', {
      pathParams: { scope },
    })
  })
}

export function useBuybackSearch(
  scope: Scope,
  options = { debounce: false, debounceTime: 0 },
) {
  const query = ref('')
  const debouncedQuery = ref('')
  const logger = useLogger()
  const algoliaSearchOption = useAlgoliaSearchOptions('buyback')

  const { data: config, error: configError } =
    getBuybackSearchConfiguartion(scope)

  const {
    data: hits,
    pending: isLoading,
    error: searchError,
  } = useAsyncData(
    () => `buyback-search-${scope}-${debouncedQuery.value}`,
    async () => {
      if (
        typeof debouncedQuery.value !== 'string' ||
        debouncedQuery.value === ''
      ) {
        return []
      }

      if (!config.value?.indexName) {
        return []
      }

      const algoliaIndex = algoliasearch(
        'dummy_app_id',
        'dummy_key',
        algoliaSearchOption,
      ).initIndex(config.value.indexName)

      const searchResult = await algoliaIndex.search<AlgoliaHit>(
        debouncedQuery.value,
        {
          hitsPerPage: 5,
          page: 0,
        },
      )

      return searchResult.hits.map(algoliaHitToBuybackProduct)
    },
    {
      immediate: false,
      default: () => [],
    },
  )

  const error = computed(() => configError.value || searchError.value)

  const performSearch = options.debounce
    ? useDebounceFn(() => {
        debouncedQuery.value = query.value
      }, options.debounceTime)
    : () => {
        debouncedQuery.value = query.value
      }

  watch(query, performSearch)

  watchEffect(() => {
    if (error.value) {
      logger.error(SWAP_INFO_MESSAGE.SWAP_SEARCH, {
        error: error.value,
        owners: ['bot-squad-circularity-offer-front'],
      })
    }
  })

  return { query, hits, isLoading, indexName: config }
}
